import { isInternalAtlasUrl, toAtlasRouterPath } from '@common/helpers/atlasUrls'

/**
 * `name` of the iframe AIM's module panel loads a record into. A frame name rather than a
 * query param because it survives every navigation the module makes inside the frame.
 */
export const ATLAS_MODULE_FRAME_NAME = 'atlas-module-panel'

/** True for the app running inside AIM's module panel, which draws the chrome around it. */
export const isInAtlasModuleFrame = (): boolean =>
  typeof window !== 'undefined' &&
  window.parent !== window &&
  window.name === ATLAS_MODULE_FRAME_NAME

const FRAME_NAVIGATE_KEY = '__atlasModuleNavigate'

interface IModuleFrameWindow extends Window {
  [FRAME_NAVIGATE_KEY]?: (path: string) => void
}

/** Lets AIM move the record in its panel through the frame's router instead of reloading it. */
export const exposeFrameNavigator = (
  navigate: (path: string, options: { replace: boolean }) => unknown,
): void => {
  if (!isInAtlasModuleFrame()) return
  ;(window as IModuleFrameWindow)[FRAME_NAVIGATE_KEY] = (path) => {
    void navigate(path, { replace: true })
  }
}

/** Moves the frame's app to `path` in place. False when it can't yet, and the frame must load it. */
export const navigateFrame = (frame: HTMLIFrameElement | null, path: string): boolean => {
  try {
    const navigate = (frame?.contentWindow as IModuleFrameWindow | null)?.[FRAME_NAVIGATE_KEY]
    if (!navigate) return false
    navigate(path)
    return true
  } catch {
    return false
  }
}

/**
 * Whether the page in the frame would refuse to unload. Asks through the same cancelable
 * `beforeunload` that `useBeforeUnload` (and so every RouteLeavingGuard) already answers.
 */
export const frameHasUnsavedChanges = (frame: HTMLIFrameElement | null): boolean => {
  const frameWindow = frame?.contentWindow
  if (!frameWindow) return false
  try {
    return !frameWindow.dispatchEvent(new Event('beforeunload', { cancelable: true }))
  } catch {
    return false
  }
}

/** The in-app path the module inside the frame has navigated to, if it is still an Atlas page. */
export const getFramePath = (frame: HTMLIFrameElement | null): string | null => {
  try {
    const href = frame?.contentWindow?.location.href
    return href && isInternalAtlasUrl(href) ? toAtlasRouterPath(href) : null
  } catch {
    return null
  }
}
